import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const err = (e) => { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); };

export function PenaltySettings() {
  const { can } = useAuth();
  const [f, setF] = useState(null);
  const [hist, setHist] = useState([]);
  const load = () => api.get("/tax/settings").then(({ data }) => { setF({ ...data.penalty, effective_date: today() }); setHist(data.penalty_history); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const ro = !can("pen.settings");
  const save = () => api.put("/tax/penalty", f).then(() => { toast.success("Penalty settings saved"); load(); }).catch(err);
  return (
    <div className="space-y-4" data-testid="penalty-settings">
      <div className="grid gap-4 sm:grid-cols-2">
        {[["denial", "Denial Penalty"], ["cancel", "Cancellation Penalty"]].map(([k, l]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between"><p className="text-sm font-medium">Enable {l}</p>
              <Switch disabled={ro} checked={!!f[`${k}_enabled`]} onCheckedChange={(v) => set(`${k}_enabled`, v)} data-testid={`${k}-enabled-switch`} /></div>
            <Label className="mt-3 block">{l} Amount (₹ per order)</Label>
            <Input disabled={ro} type="number" min="0" value={f[`${k}_rate`]} onChange={(e) => set(`${k}_rate`, e.target.value)} data-testid={`${k}-rate-input`} />
          </div>
        ))}
      </div>
      <div className="max-w-xs"><Label>Effective Date</Label><Input disabled={ro} type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid="penalty-effective-date" /></div>
      <p className="text-xs text-muted-foreground">New rates apply to activity dated on/after the effective date. Already saved records keep the rates they were calculated with.</p>
      {!ro && <Button onClick={save} data-testid="save-penalty-btn"><Save className="mr-1 h-4 w-4" /> Save Penalty Settings</Button>}
      <History rows={hist.map((h) => [h.effective_date, `₹${h.denial_rate} ${h.denial_enabled ? "" : "(off)"}`, `₹${h.cancel_rate} ${h.cancel_enabled ? "" : "(off)"}`, h.created_by, new Date(h.created_at).toLocaleString("en-IN")])}
        head={["Effective", "Denial", "Cancellation", "By", "Saved"]} />
    </div>
  );
}

const SECTIONS = { dp: ["194C", "194H", "194J", "194O", "Other"], franchise: ["194H", "194C", "194J", "Other"] };
const ENTITIES = ["Individual/HUF", "Company", "Firm/LLP", "Other"];

function TdsCard({ cat, title, cfg, ro, onSaved }) {
  const [f, setF] = useState({ ...cfg, effective_date: today() });
  useEffect(() => setF({ ...cfg, effective_date: today() }), [cfg]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = () => api.put(`/tax/tds/${cat}`, f).then(() => { toast.success(`${title} TDS saved`); onSaved(); }).catch(err);
  const sel = "mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm";
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`tds-card-${cat}`}>
      <div className="flex items-center justify-between"><p className="font-head font-bold">{title} TDS</p>
        <label className="flex items-center gap-2 text-sm">Enable TDS <Switch disabled={ro} checked={!!f.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`tds-${cat}-enabled`} /></label></div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div><Label>TDS Percentage</Label><select disabled={ro} className={sel} value={f.rate} onChange={(e) => set("rate", Number(e.target.value))} data-testid={`tds-${cat}-rate`}>
          {Array.from({ length: 10 }, (_, i) => <option key={i} value={i + 1}>{i + 1}%</option>)}</select></div>
        <div><Label>TDS Section</Label><select disabled={ro} className={sel} value={f.section} onChange={(e) => set("section", e.target.value)} data-testid={`tds-${cat}-section`}>
          {SECTIONS[cat].map((s) => <option key={s}>{s}</option>)}</select></div>
        {cat === "franchise" && <div><Label>Payee Entity Type</Label><select disabled={ro} className={sel} value={f.entity_type} onChange={(e) => set("entity_type", e.target.value)} data-testid="tds-franchise-entity">
          {ENTITIES.map((s) => <option key={s}>{s}</option>)}</select></div>}
        <div><Label>PAN missing / invalid rate (%)</Label><Input disabled={ro} type="number" value={f.no_pan_rate} onChange={(e) => set("no_pan_rate", e.target.value)} data-testid={`tds-${cat}-nopan`} /></div>
        <div><Label>Single payment threshold (₹)</Label><Input disabled={ro} type="number" value={f.single_threshold} onChange={(e) => set("single_threshold", e.target.value)} data-testid={`tds-${cat}-single`} /></div>
        <div><Label>Financial-year threshold (₹)</Label><Input disabled={ro} type="number" value={f.annual_threshold} onChange={(e) => set("annual_threshold", e.target.value)} data-testid={`tds-${cat}-annual`} /></div>
        <div><Label>Effective Date</Label><Input disabled={ro} type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid={`tds-${cat}-effective`} /></div>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">PAN status is read from each payee's profile. TDS starts once a single payment or the FY total crosses the threshold; on crossing the FY limit, earlier untaxed payments that year are caught up. Verify rates and sections with your tax advisor before enabling.</p>
      {!ro && <Button className="mt-3" onClick={save} data-testid={`save-tds-${cat}`}><Save className="mr-1 h-4 w-4" /> Save {title} TDS</Button>}
    </div>
  );
}

export function TdsSettings() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/tax/settings").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const ro = !can("tax.settings");
  return (
    <div className="space-y-4" data-testid="tds-settings">
      <div className="grid gap-4 lg:grid-cols-2">
        <TdsCard cat="dp" title="Delivery Partner" cfg={d.tds.dp} ro={ro} onSaved={load} />
        <TdsCard cat="franchise" title="Franchise Partner" cfg={d.tds.franchise} ro={ro} onSaved={load} />
      </div>
      <DeductorCard ro={ro} />
      <History head={["Type", "Effective", "Enabled", "Rate", "Section", "By"]}
        rows={d.tds_history.map((h) => [h.category === "dp" ? "DP" : "Franchise", h.effective_date, h.enabled ? "ON" : "OFF", `${h.rate}%`, h.section, h.created_by])} />
    </div>
  );
}

function DeductorCard({ ro }) {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/tax/deductor").then(({ data }) => setF(data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = () => api.put("/tax/deductor", f).then(({ data }) => { setF(data); toast.success("Deductor details saved"); }).catch(err);
  const fields = [["name", "Deductor Name"], ["tan", "TAN"], ["pan", "PAN"], ["address", "Address"], ["responsible_person", "Responsible Person"], ["designation", "Designation"]];
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="deductor-card">
      <p className="font-head font-bold">Deductor Details (for TDS certificates)</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">{fields.map(([k, l]) => (
        <div key={k}><Label>{l}</Label><Input disabled={ro} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`deductor-${k}`} /></div>))}</div>
      {!ro && <Button className="mt-3" onClick={save} data-testid="save-deductor"><Save className="mr-1 h-4 w-4" /> Save Deductor Details</Button>}
    </div>
  );
}

const History = ({ head, rows }) => rows.length === 0 ? null : (
  <div className="overflow-x-auto rounded-xl border border-border">
    <p className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Change history</p>
    <table className="w-full text-sm"><thead><tr>{head.map((h) => <th key={h} className="px-3 py-2 text-left text-xs text-muted-foreground">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border">{r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}</tr>)}</tbody></table>
  </div>
);
